import React, { useState } from 'react'

import FeatureNotEnabled from '../../components/FeatureNotEnabled'
import { Card } from '../../components/UI'
import {
    RotateCcw, Clock, HardDrive, CheckCircle2, AlertTriangle,
    Download, Upload, Trash2, Info, Settings2, Database
} from 'lucide-react'

type TabType = 'snapshots' | 'config' | 'docs'

interface Snapshot {
    id: string
    name: string
    date: string
    size: string
    type: 'auto' | 'manual'
    status: 'healthy' | 'restoring' | 'archived'
    changes: string
}

const SNAPSHOTS: Snapshot[] = [
    { id: 'snap-007', name: 'Pre-release v4.5.0', date: '2026-02-22 03:55', size: '2.4 MB', type: 'manual', status: 'healthy', changes: 'Backup manual antes de release Fase 5' },
    { id: 'snap-006', name: 'Auto — daily', date: '2026-02-22 00:00', size: '2.3 MB', type: 'auto', status: 'healthy', changes: 'Snapshot automático diario' },
    { id: 'snap-005', name: 'Auto — daily', date: '2026-02-21 00:00', size: '2.1 MB', type: 'auto', status: 'healthy', changes: 'Snapshot automático diario' },
    { id: 'snap-004', name: 'Pre-migration RLS', date: '2026-02-20 18:30', size: '1.9 MB', type: 'manual', status: 'healthy', changes: 'Antes de aplicar políticas RLS en Supabase' },
    { id: 'snap-003', name: 'Auto — daily', date: '2026-02-20 00:00', size: '1.8 MB', type: 'auto', status: 'archived', changes: 'Snapshot automático diario (archivado)' },
    { id: 'snap-002', name: 'Initial deploy', date: '2026-02-19 14:00', size: '1.5 MB', type: 'manual', status: 'archived', changes: 'Primer deploy a producción' },
]

export default function SnapshotsPage() {
    const [tab, setTab] = useState<TabType>('snapshots')
    const [selectedSnap, setSelectedSnap] = useState<string | null>(null)

    const healthyCount = SNAPSHOTS.filter(s => s.status === 'healthy').length
    const totalSize = SNAPSHOTS.reduce((acc, s) => acc + parseFloat(s.size), 0).toFixed(1)

    const statusStyles = {
        healthy: { color: 'text-neon-green', bg: 'bg-neon-green/10', label: 'Healthy' },
        restoring: { color: 'text-yellow-400', bg: 'bg-yellow-500/10', label: 'Restoring' },
        archived: { color: 'text-gray-500', bg: 'bg-gray-500/10', label: 'Archived' },
    }

    const tabs = [
        { id: 'snapshots' as const, label: 'Snapshots', icon: <Database size={16} /> },
        { id: 'config' as const, label: 'Config', icon: <Settings2 size={16} /> },
        { id: 'docs' as const, label: 'Docs', icon: <Info size={16} /> },
    ]

    return (
        <div className="p-6 max-w-5xl mx-auto pb-20">
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-white mb-1 flex items-center gap-3">
                    <RotateCcw className="text-neon-blue" /> Snapshots & Rollback
                </h1>
                <p className="text-gray-500 text-sm">Backups automáticos con rollback remoto a versiones anteriores</p>
            </div>

            <FeatureNotEnabled moduleName="Snapshots" />

            <div className="grid grid-cols-3 gap-3 mb-6">
                {[
                    { label: 'Snapshots', value: String(SNAPSHOTS.length), color: 'text-neon-blue', icon: <Database size={16} /> },
                    { label: 'Healthy', value: String(healthyCount), color: 'text-neon-green', icon: <CheckCircle2 size={16} /> },
                    { label: 'Total Size', value: `${totalSize} MB`, color: 'text-purple-400', icon: <HardDrive size={16} /> },
                ].map((s, i) => (
                    <Card key={i} className="!p-4 text-center">
                        <div className={`flex items-center justify-center gap-2 ${s.color} mb-1`}>{s.icon}</div>
                        <div className="text-2xl font-bold font-mono text-white">{s.value}</div>
                        <div className={`text-[10px] uppercase tracking-wider mt-1 ${s.color}`}>{s.label}</div>
                    </Card>
                ))}
            </div>

            <div className="flex gap-1 mb-6 bg-charcoal rounded-lg p-1 border border-white/5">
                {tabs.map(t => (
                    <button key={t.id} onClick={() => setTab(t.id)} className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-all ${tab === t.id ? 'bg-neon-blue/10 text-neon-blue border border-neon-blue/20' : 'text-gray-500 hover:text-white'}`}>
                        {t.icon} {t.label}
                    </button>
                ))}
            </div>

            {tab === 'snapshots' && (
                <div className="space-y-2">
                    {SNAPSHOTS.map(snap => {
                        const st = statusStyles[snap.status]
                        const isSelected = selectedSnap === snap.id
                        return (
                            <Card key={snap.id} className={`!p-4 cursor-pointer transition-all ${isSelected ? 'ring-1 ring-neon-blue/40' : ''}`} onClick={() => setSelectedSnap(isSelected ? null : snap.id)}>
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-lg bg-neon-blue/5 border border-neon-blue/10 flex items-center justify-center">
                                            {snap.type === 'auto' ? <Clock size={18} className="text-neon-blue" /> : <Upload size={18} className="text-purple-400" />}
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <span className="text-white font-medium text-sm">{snap.name}</span>
                                                <span className={`px-1.5 py-0.5 rounded text-[9px] font-mono ${st.bg} ${st.color}`}>{st.label}</span>
                                                <span className={`px-1.5 py-0.5 rounded text-[9px] font-mono ${snap.type === 'auto' ? 'bg-blue-500/10 text-blue-400' : 'bg-purple-500/10 text-purple-400'}`}>{snap.type}</span>
                                            </div>
                                            <div className="flex items-center gap-3 mt-0.5">
                                                <span className="text-gray-500 text-xs">{snap.date}</span>
                                                <span className="text-gray-600 text-xs">·</span>
                                                <span className="text-gray-500 text-xs">{snap.size}</span>
                                                <span className="text-gray-600 text-xs">·</span>
                                                <code className="text-gray-600 text-[10px] font-mono">{snap.id}</code>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {isSelected && (
                                    <div className="mt-3 pt-3 border-t border-white/5">
                                        <p className="text-gray-400 text-xs mb-3">{snap.changes}</p>
                                        <div className="flex gap-2">
                                            <button className="px-3 py-1.5 bg-neon-blue/10 text-neon-blue border border-neon-blue/20 rounded-lg text-xs font-medium hover:bg-neon-blue/20 transition-all flex items-center gap-1.5" onClick={e => { e.stopPropagation() }}>
                                                <RotateCcw size={12} /> Rollback
                                            </button>
                                            <button className="px-3 py-1.5 bg-gray-700/50 text-gray-400 border border-gray-600/30 rounded-lg text-xs font-medium hover:bg-gray-700 transition-all flex items-center gap-1.5" onClick={e => { e.stopPropagation() }}>
                                                <Download size={12} /> Descargar
                                            </button>
                                            {snap.status !== 'archived' && (
                                                <button className="px-3 py-1.5 bg-red-500/10 text-red-400 border border-red-500/20 rounded-lg text-xs font-medium hover:bg-red-500/20 transition-all flex items-center gap-1.5" onClick={e => { e.stopPropagation() }}>
                                                    <Trash2 size={12} /> Eliminar
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </Card>
                        )
                    })}
                </div>
            )}

            {tab === 'config' && (
                <Card>
                    <h2 className="text-white font-bold text-sm mb-4">Configuración de Snapshots</h2>
                    <div className="space-y-3">
                        {[
                            { key: 'autoEnabled', label: 'Auto-snapshot', value: 'Activado (diario a las 00:00)' },
                            { key: 'retention', label: 'Retención', value: '30 días (auto) / indefinido (manual)' },
                            { key: 'maxSnapshots', label: 'Máximo snapshots', value: '50' },
                            { key: 'storage', label: 'Almacenamiento', value: 'Supabase Storage (bucket: snapshots)' },
                            { key: 'encryption', label: 'Encriptación', value: 'AES-256-GCM at rest' },
                            { key: 'preRelease', label: 'Pre-release auto', value: 'Sí — snapshot antes de cada git tag' },
                        ].map(c => (
                            <div key={c.key} className="flex items-center justify-between p-3 bg-black/20 rounded-lg">
                                <span className="text-white text-sm">{c.label}</span>
                                <code className="text-neon-blue text-xs font-mono bg-neon-blue/5 px-2 py-1 rounded">{c.value}</code>
                            </div>
                        ))}
                    </div>
                </Card>
            )}

            {tab === 'docs' && (
                <div className="space-y-4">
                    <Card>
                        <h2 className="text-white font-bold text-sm mb-3">¿Cómo funciona el Rollback?</h2>
                        <div className="space-y-2">
                            {[
                                { step: '1', title: 'Seleccionar snapshot', desc: 'Elige el punto de restauración deseado de la lista' },
                                { step: '2', title: 'Confirmación', desc: 'Se requiere doble confirmación para evitar rollbacks accidentales' },
                                { step: '3', title: 'Backup previo', desc: 'Se crea un snapshot automático del estado actual antes de restaurar' },
                                { step: '4', title: 'Restauración', desc: 'Se restauran archivos, configuración y estado de DB al punto seleccionado' },
                                { step: '5', title: 'Verificación', desc: 'Health check post-rollback para confirmar integridad del sistema' },
                            ].map(s => (
                                <div key={s.step} className="flex items-start gap-3 p-3 bg-black/20 rounded-lg">
                                    <span className="w-6 h-6 rounded-full bg-neon-blue/10 text-neon-blue text-xs font-bold flex items-center justify-center shrink-0">{s.step}</span>
                                    <div>
                                        <span className="text-white text-sm font-medium">{s.title}</span>
                                        <p className="text-gray-500 text-xs mt-0.5">{s.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Card>
                    <Card className="bg-yellow-500/5 border border-yellow-500/20">
                        <div className="flex items-center gap-3">
                            <AlertTriangle size={20} className="text-yellow-400 shrink-0" />
                            <div>
                                <h3 className="text-yellow-400 font-bold text-sm">⚠️ Precauciones</h3>
                                <p className="text-gray-400 text-xs mt-1">Un rollback reemplaza el estado actual. Los datos creados después del snapshot se perderán. Siempre se genera un backup pre-rollback como red de seguridad.</p>
                            </div>
                        </div>
                    </Card>
                </div>
            )}
        </div>
    )
}
